import { useMemo } from "react";
import { productionTrendBars } from "../../chartWidgets";
import { summarizeProductionRhythm } from "../../heroProduction";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import type { WidgetHistoryState } from "../../useProductionHistories";
import type { ProductionHistoryWindow } from "@/features/devices/types";
import { formatWidgetNumber, formatWidgetRelative } from "./format";
import { HeroCard, HeroMeaning, HeroWindowPicker, type HeroTone } from "./HeroCard";
import { TimeSeriesChart } from "./TimeSeriesChart";
import { windowSpan } from "./windowPhrase";

interface ProductionHeroWidgetProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  /** Unidade declarada para v; ausente impede apresentar uma média sem unidade comprovada. */
  rateUnit?: string;
  history?: WidgetHistoryState;
  window: ProductionHistoryWindow;
  editable?: boolean;
  onConfigure?: () => void;
  /**
   * Desfaz o "Gerar grafico": so existe em card que nasceu de um.
   *
   * Esta face E o destino do grafico gerado a partir de um ACUMULADOR — a
   * origem mais comum de todas. Sem repassar isto, `chartOrigin` ficava
   * gravado e inalcancavel: a substituicao acontecia e nao tinha volta.
   */
  onHideChart?: () => void;
  /** Troca a janela direto no card (persistida pelo pai). */
  onChangeWindow?: (window: ProductionHistoryWindow) => void;
}

/** Frases com concordância por janela. */
const WINDOW_RECENT: Record<ProductionHistoryWindow, string> = {
  "6h": "nas últimas 6 h",
  "12h": "nas últimas 12 h",
  "24h": "nas últimas 24 h",
  "48h": "nas últimas 48 h",
  "7d": "nos últimos 7 dias",
  "30d": "nos últimos 30 dias",
};

/**
 * Cartão de referência da nova linguagem: a produção da janela como HERÓI (um
 * número que respira), uma linha de SIGNIFICADO sobre as amostras recebidas
 * e a TENDÊNCIA como FACE do mesmo cartão — não um gráfico órfão. Sem
 * caixa alta, sem pilha de badges; fala só quando há o que dizer.
 */
export function ProductionHeroWidget({
  widget,
  value,
  rateUnit,
  history,
  window,
  editable,
  onConfigure,
  onHideChart,
  onChangeWindow,
}: ProductionHeroWidgetProps) {
  const status = history?.status ?? "loading";
  // `reloading` = troca de janela em andamento: a série ANTERIOR permanece
  // visível (atenuada) até a nova chegar — nunca card em branco.
  const busy = history?.status === "reloading";
  const data =
    history?.status === "ready" || history?.status === "reloading" ? history.data : null;
  const settledStatus = busy ? "ready" : status;
  const decimals = widget.settings.decimals ?? 0;
  const unit = widget.settings.unit?.trim() || null;
  const isFresh = value.isAvailable;

  // Durante o reloading, barras/total ainda são da janela anterior — o eixo
  // do gráfico usa a janela do DADO, não a recém-pedida.
  const dataWindow = data?.window ?? window;
  const total = data?.total ?? null;
  // Memoizado como nas outras faces: sem isto cada tick ao vivo criava um
  // array novo e o memo do grafico nunca acertava nas barras.
  const bars = useMemo(() => data ? productionTrendBars(data.points, dataWindow) : [], [data, dataWindow]);
  const rhythm = data ? summarizeProductionRhythm(data, isFresh) : null;

  const noIncrement = !!data?.points.some(point => point.sampleCount > 0 &&
    (point.accFirst !== null || point.accLast !== null)) &&
    data.points.every(point => point.delta === 0);
  const meaning = resolveMeaning(settledStatus, rhythm, rateUnit?.trim() || null, noIncrement);
  const cardTone = meaning?.tone === "warn" ? "warn" : "ok";

  return (
    <HeroCard
      title={widget.title}
      caption={`Total ${WINDOW_RECENT[dataWindow]}`}
      tone={cardTone}
      editable={editable}
      onConfigure={onConfigure}
      onHideChart={onHideChart}
      footnote={footnoteFor(settledStatus, isFresh, value.measuredAt)}
    >
      <div className="w-hero__value">
        {formatWidgetNumber(total, decimals)}
        {unit ? <span className="w-hero__unit">{unit}</span> : null}
      </div>

      {meaning ? <HeroMeaning tone={meaning.tone}>{meaning.text}</HeroMeaning> : null}

      <div
        className={busy ? "w-hero__trend w-hero__trend--busy" : "w-hero__trend"}
        aria-busy={busy || undefined}
      >
        <div className="w-hero__trend-hd">
          <span>Ao longo {windowSpan(dataWindow)}</span>
          {onChangeWindow ? (
            <HeroWindowPicker
              value={window}
              busy={busy}
              onChange={onChangeWindow}
              ariaLabel={`Período de ${widget.title}`}
            />
          ) : null}
        </div>
        {data && bars.length > 0 ? (
          <>
            <TimeSeriesChart
              points={bars}
              kind="bar"
              window={dataWindow}
              unit={unit ?? undefined}
              decimals={decimals}
              yScale="auto"
              title={widget.title}
              valueLabel="total"
            />
            {bars.some((bar) => bar.unknown) ? (
              <p className="w-hero__trend-note">
                Trechos em cinza: período sem telemetria — total desconhecido.
              </p>
            ) : null}
          </>
        ) : (
          <p className="w-hero__trend-empty">
            {status === "unavailable"
              ? "Ative o Timescale para ver a tendência."
              : status === "error"
                ? "Não foi possível carregar a tendência."
                : status === "ready"
                  ? "Ainda não há amostras nesta janela."
                  : "Carregando tendência…"}
          </p>
        )}
      </div>
    </HeroCard>
  );
}

/**
 * Rodapé honesto: usa a leitura REAL (`value.measuredAt`), não o fim da janela
 * (`history.to` = agora), que dava "Atualizado há 0s" mesmo com a porta parada
 * há horas. Fresco → "Atualizado há Xs"; velho → "Última leitura há Xh".
 */
function footnoteFor(
  status: WidgetHistoryState["status"] | "loading",
  isFresh: boolean,
  measuredAt: string | null,
): string | null {
  if (status !== "ready" || !measuredAt) return null;
  const rel = formatWidgetRelative(measuredAt);
  if (!rel) return isFresh ? "Atualizado agora" : null;
  return isFresh ? `Atualizado ${rel}` : `Última leitura ${rel}`;
}

function resolveMeaning(
  status: WidgetHistoryState["status"] | "loading",
  rhythm: ReturnType<typeof summarizeProductionRhythm> | null,
  rateUnit: string | null,
  noIncrement: boolean,
): { tone: HeroTone; text: string } | null {
  if (status === "unavailable") {
    return { tone: "muted", text: "Histórico requer Timescale ativo" };
  }
  if (status === "error") {
    return { tone: "warn", text: "Não foi possível ler o histórico" };
  }
  if (status !== "ready" || !rhythm) {
    return null;
  }

  const rateText =
    rhythm.avgRate !== null && rateUnit
      ? `${formatWidgetNumber(rhythm.avgRate, 0)} ${rateUnit} · média das leituras positivas`
      : "";

  switch (rhythm.state) {
    case "producing":
    case "stopped":
      // Um contador de rejeitos/vapor/água não declara o estado da máquina.
      // A série prova incremento nas amostras; não autoriza inferir parada.
      if (noIncrement) return { tone: "muted", text: `Sem incremento nas amostras recebidas${rateText ? ` · ${rateText}` : ""}` };
      return rateText ? { tone: "ok", text: rateText } : null;
    case "stale":
      return { tone: "warn", text: "Sem leitura recente" };
    default:
      return { tone: "muted", text: "Sem leituras registradas na janela" };
  }
}
